import React from 'react';
import { GridCellProps, GridColumn as Column } from "@progress/kendo-react-grid";
import { process } from "@progress/kendo-data-query";
import { Component, Container, HBox, PuzzleButton, PuzzleInput, PuzzleTextArea, VBox } from "puzzle";
import classNames from "classnames";
import { Keys } from "@progress/kendo-react-common";
import { APPROVE_PROPS, LINE_ITEM, QUOTE, QUOTE_COMMENT, QUOTES_RESPONSE, VENDOR } from "../Types";
import Big from "big.js";
import QuoteComments from "../QuoteComments/QuoteComments";
import * as _ from "lodash";
import { calculateLineItemsTotal } from "../../../../util/Util";
import { FieldWrap } from "../../../../UI/FieldWrap/FieldWrap";
import { Field } from "../../../../UI/Field/Field";
import KendoGridAbstract from "../../../../util/KendoGridAbstract/KendoGridAbstract";
import { DialogActionsBar } from "@progress/kendo-react-dialogs";
import {
  MaskedTextBoxEvent
} from "@progress/kendo-react-inputs/dist/npm/maskedtextbox/MaskedTextBoxProps";


type STATE = {
  showComments: boolean,
  allPOsEntered: boolean,
  data: QUOTES_RESPONSE,
  changes: boolean,
  gridConfig: any,
  groupable: boolean,
  editField: string,
  currency: string,
  dataState: any,
  hasAlreadyApproved: boolean,
  showApprovedQuotes: boolean,
  comments: QUOTE_COMMENT,
  comment: string,
}

class ApproveQuotes<P, S extends Partial<STATE>> extends Component<P & APPROVE_PROPS, S & STATE> {
  poTimeout: any;
  showHideButton: any = React.createRef();

  static defaultProps = {
    userName: '',
    showFooter: true
  };

  constructor(props: APPROVE_PROPS) {
    super(props);
    this.state = {
      allPOsEntered: true,
      dataState: {},
      changes: false,
      gridConfig: { data: [] },
      groupable: false,
      editField: '',
      currency: '',
      hasAlreadyApproved: _.some(props.data?.quotes, {
        status: 'APPROVED'
      }),
      showApprovedQuotes: false,
      showComments: true,
      data: props.data,
      comments: this.updateComments(),
      comment: ''
    };
  }

  moduleDidMount = () => {
    const { data } = this.state;
    const quoteLevel = data.quoteConfiguration.quoteLevel;
    this.setState({
      gridConfig: this.createAppState({
        group: [{ field: "displayName" }]
      }, false, data),
      groupable: quoteLevel.quoteScope !== 'LANGUAGE' || quoteLevel.phaseQuotes,
      currency: data.quotes[0].currency
    })
  };

  /**
   * Amount renderer
   *
   * @param cellProps
   */
  amountRender = (cellProps: GridCellProps) => {
    let cell = (<td />);
    const { currency } = this.state;
    const { dataItem, field, rowType } = cellProps;
    if (rowType === 'data') {
      let value = null;
      if (field) {
        value = dataItem[field];
      }
      cell = (
        <td className={'k-text-right tp-text-medium pd-cell-amount'}>
          <span className={'sr-only'}>
            {this.translate(`columnHeader.${field}`) + ' '}
          </span>
          <span className={'pd-currency'}>{currency}</span>
          <span>{this.returnValueByDecimals(value)}</span>
        </td>
      );
    }

    return cell;
  };

  /**
   * Applies po number to all scopes
   *
   * @param poNumber
   */
  applyPOToAllScopes = (poNumber: number) => {
    this.exitEdit();
    const { data, showApprovedQuotes } = this.state;
    data.quotes.forEach((currentQuote: any) => {
      if (currentQuote.status !== 'APPROVED') {
        currentQuote.poNumber = poNumber
      }
    });
    const gridConfig = this.createAppState({
      group: [{ field: "displayName" }]
    }, showApprovedQuotes, data);
    this.setState({
      gridConfig,
      data
    })
  };

  /**
   * Cell render
   *
   * @param tdElement
   * @param cellProps
   */
  cellRender = (tdElement: any, cellProps: any) => {
    const { currency } = this.state;
    const child = tdElement.props.children?.props.children[0];
    let expandTag = child ? React.cloneElement(child, { ...child.props, tabIndex: 0 }) : null;

    let children = tdElement.props.children;
    if (tdElement?.props.children && cellProps.rowType === "groupHeader") {
      children = (
        <div>
          <div>
            <span>{expandTag}</span>
            <span className={'tp-quote-language'}>{tdElement.props.children?.props.children[1]}</span>
          </div>
          <div className={'pd-total-wrapper'}>
            <span>{this.translate('totalLabel')}</span>
            <span className={'pd-amount-wrap'}>
              <span className={'pd-total-currency tp-text-regular'}>{currency}</span>
              <span
                className={'pd-total-amount tp-text-medium tp-font-size-md'}>{this.returnValueByDecimals(cellProps.dataItem?.aggregates?.amount.sum)}</span>
            </span>
          </div>
        </div>
      );

    }
    return React.cloneElement(tdElement, {
      ...tdElement.props,
    }, children);
  };

  /**
   * Returns app state
   *
   * @param dataState
   * @param withApproved
   * @param originalData
   */
  createAppState = (dataState: any, withApproved: boolean, originalData: any) => {
    const groups = dataState.group;
    const quoteLevel = this.props.data.quoteConfiguration.quoteLevel;
    const grouping = quoteLevel.quoteScope !== 'LANGUAGE' || quoteLevel.phaseQuotes;
    if (groups && grouping) {
      groups.map((group: any) => group.aggregates = [{ field: 'amount', aggregate: 'sum' }]);
    }
    let data: any = this.updateData(withApproved, originalData);
    if (grouping) {
      data = process(data, dataState);
      const { data: configData } = this.state.gridConfig;
      if (configData && _.some(configData.data, (currentConfigData: any) => currentConfigData.expanded === false)) {
        this.preserveCollapsedGroups(configData, data);
      }
    }

    return {
      data,
      dataState: dataState
    };
  };

  /**
   * Default renderer
   *
   * @param cellProps
   */
  defaultRender = (cellProps: GridCellProps) => {
    const { dataItem, field, rowType } = cellProps;
    let cell = (<td />);
    if (rowType !== 'groupHeader') {
      let value = '';
      if (field) {
        value = dataItem[field];
      }
      cell = (
        <td>
          <span className={'sr-only'}>
            {this.translate(`columnHeader.${field}`) + ' '}
          </span>
          {value}
        </td>
      );
    }

    return cell;
  };

  /**
   * Handles edit on edit mode
   *
   * @param dataItem
   * @param field
   */
  enterEdit = (dataItem: any, field: any) => {
    if (field === 'poNumber' && dataItem.status !== "APPROVED") {
      const { showApprovedQuotes, data } = this.state;
      data.quotes.forEach((quote: any) => {
        if (quote.quoteScope.scopeId === dataItem.scopeId) {
          quote.inEdit = field;
        }
      });
      this.setState({
        gridConfig: this.createAppState({
          group: [{ field: "displayName" }]
        }, showApprovedQuotes, data),
        editField: field
      });
    }
  };

  /**
   * Handles edit on exit
   */
  exitEdit = () => {
    const { data, showApprovedQuotes } = this.state;
    data.quotes.map((quote: any) => quote.inEdit = '');
    this.setState({
      gridConfig: this.createAppState({
        group: [{ field: "displayName" }]
      }, showApprovedQuotes, data),
      editField: '',
      data
    });
  };

  /**
   * Fires exitEdit on each click except click on button (Apply to all)
   *
   * @param e
   */
  exitEditWithCondition = (e: MaskedTextBoxEvent) => {
    // @ts-ignore
    const relatedTarget = e.nativeEvent.relatedTarget;
    if (!relatedTarget?.className.includes('pd_applyToAll')) {
      this.exitEdit();
    }
  };

  /**
   * Handle expand change
   *
   * @param event
   */
  expandChange = (event: any) => {
    event.dataItem[event.target.props.expandField] = event.value;
    let gridConfig = _.cloneDeep(this.state.gridConfig);

    this.setState({ gridConfig })
  };

  /**
   * Returns field name regarding quoteLevel
   */
  getFieldName = () => {
    const { quoteLevel } = this.props.data.quoteConfiguration;
    let fieldName = "phaseName";
    if (quoteLevel.quoteScope === "JOB" && !quoteLevel.phaseQuotes) {
      fieldName = "batchName";
    }
    return fieldName;
  };

  /**
   * Data for post
   */
  getDataForPost = (): any => {
    const { submissionId } = this.props;
    const { comment, data } = this.state;
    let quotes = _.reduce(data.quotes, this.getQuotesForPost, []);
    let dataForPost = {
      submissionId,
      comment: comment || null,
      quotes
    };
    return dataForPost;
  };

  /**
   * Returns total from each record
   *
   * @param count
   * @param record
   */
  getGrandTotal = (count: number, record: any) => {
    count += record.quotes[0]?.approvedQuoteGrandTotal || 0;
    return count;
  };

  /**
   * Quotes for post
   *
   * @param array
   * @param quote
   */
  getQuotesForPost = (array: any[], quote: QUOTE) => {
    if (quote.approved) {
      array.push({
        quoteId: quote.quoteId,
        approvedLineItems: quote.lineItems.reduce(this.getLineItemsForPost, []),
        poNumber: quote.poNumber || null
      });
    }
    return array;
  };

  /**
   * Line item names for post
   *
   * @param LIArray
   * @param lineItem
   */
  getLineItemsForPost = (LIArray: string[], lineItem: LINE_ITEM) => {
    if (lineItem.approved) {
      LIArray.push(lineItem.name);
    }
    return LIArray;
  };

  /**
   * Returns string based on quote level
   */
  getQuoteLevel = () => {
    const { quoteLevel } = this.props.data.quoteConfiguration;
    let fieldName = "phaseName";
    if (quoteLevel.quoteScope === "JOB") {
      if (quoteLevel.phaseQuotes) {
        fieldName = "job";
      } else {
        fieldName = "batchName"
      }
    }
    return fieldName;
  };

  /**
   * Returns vendor name by passed vendor ID
   *
   * @param vendroId
   */
  getVendorNameById = (vendroId: number) => {
    const { vendors } = this.props.data;
    const vendorName = _.find(vendors, (currentVendor: VENDOR) => currentVendor.vendorId === vendroId)?.vendorName;
    return vendorName;
  };

  /**
   * If po is mandatory, all POs per quote must be entered
   */
  isApproveAllowed = () => {
    const { poMandatory } = this.props.data.quoteConfiguration;
    let isAllowed = true;
    if (poMandatory) {
      isAllowed = !(_.filter(this.state.data.quotes, (quote: QUOTE) => {
        return (quote.status === 'APPROVED' || quote.approved) && !quote.poNumber;
      }).length);
      if (!isAllowed) {
        this.setState({ allPOsEntered: false })
      }
    }
    return isAllowed;
  };

  /**
   * Phase name renderer
   *
   * @param cellProps
   */
  jobRender = (cellProps: GridCellProps) => {
    const { dataItem, rowType } = cellProps;
    let value;
    switch (this.getQuoteLevel()) {
      case 'batchName':
        value = dataItem['batchName'];
        break;
      case 'job':
        value = (<>
          <span className={'tp-quote-batch-label tp-color-accent'}>
            {dataItem['batchName']}
          </span>
            {dataItem['phaseName'] && <span> | </span>}
            {dataItem['phaseName']}
          </>
        );
        break;
      case 'phaseName':
        value = dataItem['phaseName'];
        break;
      default:
        break;
    }

    let cell = (
      <td>
          <span className={'sr-only'}>
            {this.translate(`columnHeader.${this.getQuoteLevel()}`) + ' '}
          </span>
        {value}
      </td>
    );

    return rowType !== 'groupHeader' ? cell : null
  };

  /**
   * controlling text area value and handling on change event
   * * @param event
   */
  handleChangeTextArea = (event: any) => {
    const { value } = event;
    this.setState({ comment: value })
  };

  /**
   * Language renderer
   *
   * @param cellProps
   */
  languageCellRenderer = (cellProps: GridCellProps) => {
    const { dataItem, field, rowType } = cellProps;
    let value = '';
    if (field) {
      value = dataItem.displayName;
    }

    let languageCell = (
      <td className={'tp-text-medium pd-language-cell'}>
        {!this.state.groupable && <>
          <span className={'sr-only'}>
            {this.translate(`columnHeader.${field}`) + ' '}
          </span>
          {value}
        </>}
      </td>
    );

    return rowType !== 'groupHeader' ? languageCell : null;
  };

  /**
   * Line item renderer
   *
   * @param cellProps
   */
  lineItemsRender = (cellProps: GridCellProps) => {
    const { currency } = this.state;
    const { dataItem, field, rowType } = cellProps;
    let value: LINE_ITEM[] = [];
    let lineItems: LINE_ITEM[] = [];
    if (field && dataItem.quotes) {
      lineItems = dataItem.quotes[0]?.lineItems;
      value = lineItems.filter((item: any) => item.isOptional)
    }
    const { quoteConfiguration } = this.props.data;
    let languageCell = (
      <td className={'tp-line-items-column'}>
        <span className={'sr-only'}>
          {this.translate(`columnHeader.${field}`) + ' '}
        </span>
        {value.map((lineItem: LINE_ITEM, index: number) => {
          const { name, rate, quantity, approved, unit, accepted } = lineItem;
          let total = Big(0);
          if (rate && quantity) {
            if (unit === 'PERCENTAGE') {
              let totalWithoutPercentage = calculateLineItemsTotal(lineItems, false, true, false);
              total = Big(rate).div(100).times(totalWithoutPercentage);
            } else {
              total = Big(rate).times(quantity);
            }
          }
          return (
            <div key={index} className={'k-hbox k-align-items-center tp-line-item-wrap'}>
              <div className="tp-line-item-name">{name}</div>
              <HBox
                alignItemsEnd
                className={`pd-line-item-value ${accepted || approved ? 'tp-approved' : 'tp-rejected'}`}
                justifyContentBetween
              >
                <span>{currency}</span>
                <span
                  className={'tp-text-medium'}>{this.returnValueByDecimals(total.round(quoteConfiguration.numberPrecision).toNumber())}</span>
              </HBox>
              <i aria-hidden="true"
                 className={`tp-icon tp-icon-sm ${accepted || approved ? 'tp-check' : 'tp-close'}`} />
            </div>
          );
        })}
      </td>
    );

    return rowType !== 'groupHeader' ? languageCell : null;
  };

  /**
   * Updates data after user stop typing po number
   *
   * @param dataItem
   */
  onPoChangeCallback = (dataItem: any) => {
    clearTimeout(this.poTimeout);
    this.poTimeout = setTimeout(() => {
      const { data } = this.state;
      data.quotes.map((quote: any) => {
        if (quote.quoteScope.scopeId === dataItem.scopeId) {
          quote.poNumber = dataItem.poNumber;
        }
        return quote;
      });
    }, 100);

  };

  /**
   * Po number render
   *
   * @param cellProps
   */
  poNumberRenderer = (cellProps: GridCellProps) => {
    const { allPOsEntered } = this.state;
    let dataItem = cellProps.dataItem;
    let field = cellProps.field || '';
    const { rowType } = cellProps;
    const inEdit = dataItem.inEdit && this.state.editField === field;
    let tdCls = classNames(
      '',
      {
        'tp-in-edit': inEdit
      }
    );
    const displayValue = dataItem[field];
    const disabled = dataItem.status === 'APPROVED';
    let poNumberCell = (
      <td className={`${tdCls} pd-po-number-cell`}
          aria-label={this.translate(`columnHeader.${field}`) + ' ' + displayValue}>
        <div className="k-hbox">
          {inEdit ? (
            <>
              <PuzzleInput
                actionBtnSettings={[{
                  buttonType: 'copy',
                  className: 'pd_applyToAll',
                  disabled,
                  iconClass: 'tp-icon tp-copy-all',
                  fillMode: 'flat',
                  onClick: () => this.applyPOToAllScopes(displayValue),
                  titleText: this.translate('applyToAll'),
                  withTooltip: true
                }]}
                inputClassName={'k-flex'}
                ref={(input: any) => input?.inputReference.focus()}
                size={'medium'}
                onBlur={this.exitEditWithCondition}
                onKeyDown={(event: any) => {
                  if (event.keyCode === Keys.enter) {
                    this.exitEdit();
                  }
                }}
                onChange={(e: any) => {
                  dataItem[field] = e.value;
                  this.setState({ changes: true }, this.onPoChangeCallback.bind(this, dataItem));
                }}
                required={this.props.data.quoteConfiguration.poMandatory}
                value={displayValue || ''}
              />
            </>
          ) : (
            <>
              <div
                aria-hidden={'true'}
                role={'application'}
                className={`tp-po-number-holder k-d-flex k-flex-1 k-align-items-center ${!allPOsEntered && !displayValue && 'k-state-invalid'}`}
                aria-label={displayValue}
                tabIndex={0}
                onKeyDown={(event) => {
                  const { keyCode } = event;
                  if (keyCode === Keys.enter) {
                    this.enterEdit(dataItem, field)
                  }
                }}
                onClick={() => {
                  this.enterEdit(dataItem, field);
                }}>
                {displayValue}
                <PuzzleButton
                  disabled={disabled}
                  iconClass={'tp-icon tp-copy-all'}
                  fillMode={'flat'}
                  onClick={() => {
                    this.applyPOToAllScopes(displayValue)
                  }}
                  titleText={this.translate('applyToAll')}
                  tooltipPosition={'left'}
                  withTooltip
                />
              </div>
            </>
          )}
        </div>
      </td>
    );

    return rowType !== 'groupHeader' ? poNumberCell : <td />;
  };

  /**
   * All collapsed groups should stayed collapsed after update
   *
   * @param configData
   * @param data
   */
  preserveCollapsedGroups = (configData: any, data: any) => {
    configData.data?.forEach((currentData: any) => {
      if (currentData.expanded === false) {//might be false or non
        data.data.forEach((currentDataForUpdate: any) => {
            if (currentDataForUpdate.value === currentData.value) {
              currentDataForUpdate.expanded = false;
            }
          }
        )
      }
    })
  };

  /**
   * Returns currency by passed number
   *
   * @param value
   */
  returnValueByDecimals = (value: number = 0) => {
    const { quoteConfiguration } = this.props.data;
    return Big(value).toFixed(quoteConfiguration.numberPrecision);
  };

  /**
   * Row render
   *
   * @param trElement
   * @param rowProps
   */
  rowRender = (trElement: any, rowProps: any) => {
    const trProps = {
      ...trElement.props,
    };
    let dataQa: string | undefined;
    if (rowProps.rowType === 'data') {
      const { languageName, batchName, phaseName } = rowProps.dataItem
      dataQa = [languageName, batchName, phaseName].filter(Boolean).join(',')
    }
    return React.cloneElement(trElement, {
      ...trProps,
      className: `${trProps.className} ${rowProps.dataItem?.status === 'APPROVED' && 'tp-approved'}`,
      "data-qa": dataQa
    }, trElement.props.children);
  };

  /**
   * Show / Hide approved quotes
   */
  showHideApprovedQuotes = () => {
    const { data, showApprovedQuotes } = this.state;
    this.setState({
      gridConfig: this.createAppState({
        group: [{ field: "displayName" }]
      }, !showApprovedQuotes, data),
      showApprovedQuotes: !showApprovedQuotes
    })
  };

  /**
   * Update comments data to fulfill required structure
   */
  updateComments = () => {
    const { commentsData = [] } = this.props;
    let item = commentsData.length;
    let item1;
    let comments = [];
    let currentCommentDto;
    while (item--) {
      currentCommentDto = commentsData[item].commentsDto;
      item1 = currentCommentDto.length;
      while (item1--) {
        comments.push({
          ...currentCommentDto[item1]
        });
      }
    }
    return comments;
  };

  /**
   * Adding vendor name per each record
   *
   * @param withApproved - If condition evaluates to true, data will be populated with previously approved quotes (status === 'APPROVED'). Without otherwise
   * @param quotesData
   */
  updateData = (withApproved: boolean, quotesData: any) => {
    let parsedScopes = [];
    let quotes = quotesData.quotes;
    let quotesLength = quotes.length;
    let i = 0;
    let currentQuotes;
    let additionalCondition;
    const { quoteConfiguration } = this.props.data;
    while (i < quotesLength) {
      currentQuotes = quotes[i];
      additionalCondition = withApproved && currentQuotes.status === 'APPROVED';
      if (currentQuotes && (additionalCondition || currentQuotes.approved)) {
        parsedScopes.push({
          ...currentQuotes.quoteScope,
          amount: calculateLineItemsTotal(currentQuotes.lineItems, true, true, false).round(quoteConfiguration.numberPrecision).toNumber(),
          displayName: currentQuotes.quoteScope.languageName,
          poNumber: currentQuotes.poNumber || null,
          inEdit: currentQuotes.inEdit,
          quotes: [currentQuotes],
          quoteId: currentQuotes.quoteId,
          vendorName: this.getVendorNameById(currentQuotes.vendorId),
          status: currentQuotes.status === 'APPROVED' ? 'APPROVED' : null
        })
      }
      i++;
    }
    return parsedScopes;
  };

  renderBody() {
    const {
      gridConfig = {},
      groupable,
      data,
      showApprovedQuotes,
      comments,
      hasAlreadyApproved,
      showComments
    } = this.state;
    const { userName, grandTotal, currency, showFooter } = this.props;
    const { quoteLevel, poEnabled, poMandatory } = data.quoteConfiguration;
    let grouping = {};
    if (groupable) {
      grouping = { ...gridConfig.dataState, groupable: true }
    }

    const gridCls = classNames(
      '',
      {
        'without-po': !poEnabled,
        'without-batches': !(quoteLevel.phaseQuotes || quoteLevel.quoteScope === "JOB")
      }
    )
    return (
      <>
        <div
          className={`tp-grid-wrap ${!!comments.length ? 'comments-bellow' : ''} ${hasAlreadyApproved ? 'tp-show-hide' : ''}`}>
          {gridConfig.data && (
            <KendoGridAbstract
              captionText={this.translate('SRL-approveQuotesTable')}
              className={gridCls}
              data={gridConfig.data}
              {...grouping}
              onExpandChange={this.expandChange}
              expandField="expanded"
              cellRender={this.cellRender}
              rowRender={this.rowRender}
            >
              <Column
                field="displayName"
                title={this.translate('columnHeader.languageName')}
                width={`${poEnabled || !(quoteLevel.phaseQuotes || quoteLevel.quoteScope === "JOB") ? '165px' : 'unset'}`}
                cell={this.languageCellRenderer}
                headerClassName={'tp-language-header-cell'}
              />
              {(quoteLevel.phaseQuotes || quoteLevel.quoteScope === "JOB") &&
                <Column
                  field={this.getFieldName()}
                  title={this.translate(`columnHeader.${this.getQuoteLevel()}`)}
                  width={"153px"}
                  cell={this.jobRender}
                />
              }
              <Column
                field="vendorName"
                title={this.translate('columnHeader.vendorName')}
                width={`${poEnabled || !(quoteLevel.phaseQuotes || quoteLevel.quoteScope === "JOB") ? '256px' : 'unset'}`}
                cell={this.defaultRender}
              />
              <Column
                field="optionalItems"
                title={this.translate('columnHeader.optionalItems')}
                width={`${poEnabled || !(quoteLevel.phaseQuotes || quoteLevel.quoteScope === "JOB") ? '250px' : 'unset'}`}
                cell={this.lineItemsRender}
              />
              {poEnabled &&
                <Column
                  field="poNumber"
                  headerClassName={`${poMandatory && 'k-mandatory'}`}
                  title={this.translate('columnHeader.poNumber')}
                  width={!(quoteLevel.phaseQuotes || quoteLevel.quoteScope === "JOB") ? '' : "206px"}
                  cell={this.poNumberRenderer}
                />
              }
              <Column
                field="amount"
                headerClassName={'k-text-right'}
                title={this.translate('columnHeader.amount')}
                width={`${poEnabled || !(quoteLevel.phaseQuotes || quoteLevel.quoteScope === "JOB") ? '120px' : 'unset'}`}
                cell={this.amountRender}
              />
            </KendoGridAbstract>)
          }
          {hasAlreadyApproved && <div className={'tp-show-hide-btn-wrap'}>
            <PuzzleButton
              togglable={true}
              themeColor={'primary'}
              fillMode={"flat"}
              iconClass={`tp-icon ${showApprovedQuotes ? 'tp-eye-closed' : 'tp-eye-open'}`}
              ref={(el: any) => this.showHideButton = el}
              onClick={this.showHideApprovedQuotes}
            >
              {this.translate(showApprovedQuotes ? "hideApprovedQuotes" : "showApprovedQuotes")}
            </PuzzleButton>
          </div>}
        </div>
        <VBox className={'tp-dialog-bottom-section'} gap={4}>
          {!!comments.length && <QuoteComments comments={comments} userName={userName} />}
          {showComments && <FieldWrap>
            <Field>
              <div aria-describedby={'approveQuoteTextArea'}>
                <span
                  id={'approveQuoteTextArea'}
                  className={'k-label sr-only'}
                >
                  {this.translate('textAreaPlaceholder')}
                </span>
                <PuzzleTextArea
                  label={this.translate('textAreaLabel')}
                  onChange={this.handleChangeTextArea}
                />
              </div>
            </Field>
          </FieldWrap>}
        </VBox>
        {showFooter && (
          <DialogActionsBar>
            <div className={'tp-dialog-footer-toolbar tp-left-and-right'}>
              <PuzzleButton
                onClick={() => this.dispatch('closeDialog')}
                fillMode={'flat'}>
                {this.translate('cancel')}
              </PuzzleButton>
              <Container className={'k-flex-1'} />
              <VBox alignItemsEnd>
              <span className={'tp-text-medium pd-grand-total-label tp-color-light'}>
                {this.translate('quoteTotal')}
              </span>
                <span className={'pd-grand-total-wrap'}>
                <span className={'pd-currency tp-color-light'}>{currency}</span> <span
                  className={'tp-text-medium tp-color-accent tp-font-size-md'}>{grandTotal}</span>
              </span>
              </VBox>
              <PuzzleButton
                onClick={() => this.dispatch('approveQuote')}
                themeColor={'primary'}>
                {this.translate('approveQuote')}
              </PuzzleButton>
            </div>
          </DialogActionsBar>
        )}
      </>
    );
  }
}

export default ApproveQuotes;
